import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { boxAPI } from '../utils/apiClient';
import MysteryBox from '../components/MysteryBox';
import LoadingSpinner from '../components/LoadingSpinner';
import '../styles/HomePage.css';

const HomePage = () => {
  const [boxes, setBoxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBoxes();
  }, []);

  const fetchBoxes = async () => {
    try {
      const response = await boxAPI.getAll();
      const data = Array.isArray(response.data) ? response.data : (response.data?.boxes || []);
      setBoxes(data);
      setError(null);
    } catch (error) {
      console.error('Error fetching boxes:', error?.message || error);
      // MVP fallback so homepage works without API
      const fallback = [
        {
          id: 1,
          name: 'Starter Mystery Box',
          price: 10.0,
          description: 'Perfect for beginners! Contains 3-5 jewelry items worth $20-30.',
          imageUrl: '/images/starter-box.jpg',
          category: 'Beginner',
          openedBoxes: 42,
          items: new Array(4).fill(null)
        },
        {
          id: 2,
          name: 'Premium Mystery Box',
          price: 25.0,
          description: 'High-quality jewelry pieces worth $50-75.',
          imageUrl: '/images/premium-box.jpg',
          category: 'Premium',
          openedBoxes: 89,
          items: new Array(6).fill(null)
        },
        {
          id: 3,
          name: 'Luxury Mystery Box',
          price: 50.0,
          description: 'Exclusive luxury jewelry worth $100-150.',
          imageUrl: '/images/luxury-box.jpg',
          category: 'Luxury',
          openedBoxes: 156,
          items: new Array(9).fill(null)
        }
      ];
      setBoxes(fallback);
      setError(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading mystery boxes..." />;
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Oops! Something went wrong</h2>
        <p>{error}</p>
        <button onClick={fetchBoxes} className="retry-btn">Try Again</button>
      </div>
    );
  }

  return (
    <div className="homepage">
      <div className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">
            🎁 Mystery Box Platform
          </h1>
          <p className="hero-subtitle">
            Discover amazing items in themed mystery boxes. 
            Open boxes with exciting slot-machine animations and build your collection!
          </p>
          <div className="hero-features">
            <div className="feature">
              <span className="feature-icon">🎰</span>
              <span>Slot-machine animations</span>
            </div>
            <div className="feature">
              <span className="feature-icon">💎</span>
              <span>Rare item collecting</span>
            </div>
            <div className="feature">
              <span className="feature-icon">💰</span>
              <span>Sell items for credits</span>
            </div>
          </div>
          <div className="hero-cta">
            <Link to="/register" className="cta-btn primary">
              Get Started - Free $10 Bonus!
            </Link>
            <Link to="/login" className="cta-btn secondary">
              Login
            </Link>
          </div>
        </div>
      </div>

      <div className="boxes-section">
        <div className="container">
          <h2 className="section-title">Available Mystery Boxes</h2>
          
          {boxes.length === 0 ? (
            <div className="empty-state">
              <h3>No mystery boxes available</h3>
              <p>Check back soon for new boxes!</p>
            </div>
          ) : (
            <div className="boxes-grid">
              {boxes.map((box) => (
                <div key={box.id} className="box-card-container">
                  <Link to={`/box/${box.id}`} className="box-link" state={{ box }}>
                    <div className="preview-box">
                      <div className="box-image-container">
                        {box.imageUrl ? (
                          <img src={box.imageUrl} alt={box.name} className="box-image" />
                        ) : (
                          <div className="box-placeholder">
                            <span className="box-emoji">🎁</span>
                          </div>
                        )}
                        <div className="box-category-badge">{box.category}</div>
                      </div>
                      
                      <div className="box-info">
                        <h3 className="box-name">{box.name}</h3>
                        <p className="box-description">{box.description}</p>
                        <div className="box-price">
                          <span className="price-value">${parseFloat(box.price).toFixed(2)}</span>
                        </div>
                        <div className="box-stats">
                          <span className="stat">
                            📦 {box.openedBoxes || 0} opened
                          </span>
                          <span className="stat">
                            🎁 {box.items?.length || 0} possible items
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="info-section">
        <div className="container">
          <h2>How It Works</h2>
          <div className="steps-grid">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Choose a Box</h3>
              <p>Browse our collection of themed mystery boxes</p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>Purchase & Open</h3>
              <p>Buy a box and experience our slot-machine animation</p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>Collect & Trade</h3>
              <p>Build your inventory and sell items for credits</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
